class BlogHub {
  static ALL = 'all';
  static MOBILE = '(max-width: 989px)';

  static SWIPER_OPTIONS = {
    slidesPerView: 'auto',
    spaceBetween: 40,
    grabCursor: true,
    freeMode: true,
    watchOverflow: true,
    observer: true,
    observeParents: true,
  };

  constructor(root) {
    this.root = root;
    this.batchSize = Number(root.dataset.batchSize) || 20;
    this.featureIndex = Number(root.dataset.featureIndex ?? 6);
    this.heroFollowsTab = root.dataset.heroFollowsTab === 'true';

    this.tabs = [...root.querySelectorAll('[data-blog-hub-tab]')];
    this.items = [...root.querySelectorAll('[data-blog-hub-item]')];
    this.heroes = [...root.querySelectorAll('[data-blog-hub-hero]')];
    this.loadMoreButton = root.querySelector('[data-blog-hub-load-more]');
    this.emptyState = root.querySelector('[data-blog-hub-empty]');
    this.tabsSlider = root.querySelector('[data-blog-hub-tabs]');
    this.tabsTrack = root.querySelector('[data-blog-hub-tabs-track]');
    this.tabsSwiper = null;

    this.activeBlog = BlogHub.ALL;
    this.shown = this.batchSize;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectBlog(tab.dataset.blogHubTab));
    });

    this.loadMoreButton?.addEventListener('click', () => this.loadMore());

    this.mobile = window.matchMedia(BlogHub.MOBILE);
    this.mobile.addEventListener('change', () => this.syncLayout());

    this.syncLayout();
  }

  get isMobile() {
    return this.mobile.matches;
  }

  get visibleItems() {
    const heroArticleId = this.heroArticleId;

    return this.items.filter((item) => this.matches(item, heroArticleId));
  }

    get heroArticleId() {
    if (this.isMobile) return undefined;

    return this.heroes.find((hero) => !hero.hidden)?.dataset.articleId;
  }

  matches(item, heroArticleId = this.heroArticleId) {
    if (heroArticleId && item.dataset.articleId === heroArticleId) return false;

    return this.activeBlog === BlogHub.ALL || item.dataset.blog === this.activeBlog;
  }

  syncLayout() {
    if (this.isMobile) {
      this.enableTabsSlider();
    } else {
      this.disableTabsSlider();
    }

    this.render();
  }

    enableTabsSlider() {
    if (!this.tabsSlider || !this.tabsTrack || this.tabsSwiper) return;

    this.tabsSlider.classList.add('swiper', 'text-swiper-container');
    this.tabsTrack.classList.add('swiper-wrapper');
    this.tabs.forEach((tab) => tab.classList.add('swiper-slide'));

    whenSwiperReady(() => {
      if (!this.isMobile || this.tabsSwiper) return;

      this.tabsSwiper = new Swiper(this.tabsSlider, BlogHub.SWIPER_OPTIONS);
    });
  }

  disableTabsSlider() {
    if (!this.tabsSlider || !this.tabsTrack) return;

    this.tabsSwiper?.destroy(true, true);
    this.tabsSwiper = null;

    this.tabsSlider.classList.remove('swiper', 'text-swiper-container');
    this.tabsTrack.classList.remove('swiper-wrapper');
    this.tabs.forEach((tab) => tab.classList.remove('swiper-slide'));
  }

  selectBlog(handle) {
    if (handle === this.activeBlog) return;

    this.activeBlog = handle;
    this.shown = this.batchSize;

    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.blogHubTab === handle));
    });

    const heroTarget = this.heroFollowsTab ? handle : BlogHub.ALL;
    this.heroes.forEach((hero) => {
      hero.hidden = hero.dataset.blogHubHero !== heroTarget;
    });

    this.render();
  }

  loadMore() {
    const firstNewItem = this.visibleItems[this.shown];
    this.shown += this.batchSize;
    this.render();
    firstNewItem?.focus();
  }

  render() {
    const visible = this.visibleItems;

    this.items.forEach((item) => {
      item.hidden = true;
      item.classList.remove('blog-hub__item--feature');
    });

    visible.forEach((item, index) => {
      item.hidden = index >= this.shown;
      item.classList.toggle('blog-hub__item--feature', !this.isMobile && index === this.featureIndex);
    });

    if (this.loadMoreButton) this.loadMoreButton.hidden = visible.length <= this.shown;
    if (this.emptyState) this.emptyState.hidden = visible.length > 0;
  }
}

const whenSwiperReady = (callback) => {
  if (typeof Swiper !== 'undefined') {
    callback();
    return;
  }

  const event = document.readyState === 'loading' ? 'DOMContentLoaded' : 'load';
  const target = event === 'load' ? window : document;

  target.addEventListener(
    event,
    () => {
      if (typeof Swiper !== 'undefined') callback();
    },
    { once: true }
  );
};

const initBlogHubs = () => {
  document.querySelectorAll('[data-blog-hub]').forEach((root) => {
    if (root.dataset.blogHubReady) return;
    root.dataset.blogHubReady = 'true';
    new BlogHub(root);
  });
};

initBlogHubs();

document.addEventListener('shopify:section:load', initBlogHubs);
